<script setup lang="ts">
import { useWindowSize } from '@vueuse/core';
import type { DialogProps } from '../../organisms';
import { DATA_TABLE_SIZE, useDataTableFilters } from '@DataTableModule';
import { useActiveFilter } from '@FilterModule';

defineEmits<{
  onApplyFilters: [];
  onClearFilters: [];
  dateRangeChanged: [{ startDate: string; endDate: string }];
}>();

withDefaults(
  defineProps<{
    adminColumns?: string[];
    adminColumnsVisible?: string[];
    dialog?: DialogProps;
    loading?: boolean;
    hasDataTableFilters?: boolean;
    hasFilters?: boolean;
    hasSearchInput?: boolean;
  }>(),
  {
    hasFilters: true,
    hasSearchInput: true,
  }
);

const { height, width } = useWindowSize();
const { activeFilters } = useActiveFilter();
const { activeColumns, initialColumns, searchQuery, size } = useDataTableFilters();
</script>

<template>
  <Accordion
    v-if="width < 768 || height < 576"
    class="my-3 rounded-md border"
    pt:headeraction="!p-2 !bg-gray-100"
    pt:content="!pb-0 !px-2"
  >
    <AccordionTab>
      <template #header>
        <span class="flex w-full items-center gap-2">
          <i class="pi pi-filter"></i>

          <span class="text-sm font-medium text-gray-600">
            {{ $t('filters.filtersAndActions') }}
          </span>
        </span>
      </template>

      <div class="my-4 flex flex-wrap justify-between gap-2">
        <div class="flex flex-wrap items-center gap-2">
          <div class="flex flex-wrap gap-2">
            <MSearchInput
              v-if="hasSearchInput"
              v-model="searchQuery"
              :loading
            />

            <OFilters
              v-if="activeFilters?.length && hasFilters"
              :dialog
              :loading
              :filters="activeFilters"
              @onApplyFilters="$emit('onApplyFilters')"
              @onClearFilters="$emit('onClearFilters')"
            />

            <slot name="slotLeft" />
          </div>
        </div>

        <div class="flex flex-wrap gap-2 border-t pt-2 md:border-none md:pt-0">
          <slot />

          <div
            v-if="hasDataTableFilters"
            class="flex gap-2"
          >
            <MButtonDropdown
              v-model="size"
              tooltip="components.dataTable.tableSizeDropdown.tooltip"
              :loading
              :items="DATA_TABLE_SIZE"
            />

            <MButtonShowHide
              v-model="activeColumns"
              :adminColumns
              :adminColumnsVisible
              :loading
              :options="initialColumns"
            />
          </div>

          <slot name="slotRight" />
        </div>
      </div>
    </AccordionTab>
  </Accordion>

  <div
    v-else
    class="my-4 flex flex-wrap justify-between gap-2"
  >
    <div class="flex flex-wrap items-center gap-2">
      <div class="flex flex-wrap gap-2">
        <MSearchInput
          v-if="hasSearchInput"
          v-model="searchQuery"
          :loading
        />

        <OFilters
          v-if="activeFilters?.length && hasFilters"
          :dialog
          :loading
          :filters="activeFilters"
          @onApplyFilters="$emit('onApplyFilters')"
          @onClearFilters="$emit('onClearFilters')"
        />

        <slot name="slotLeft" />
      </div>
    </div>

    <div class="flex flex-wrap gap-2">
      <slot />

      <div
        v-if="hasDataTableFilters"
        class="flex flex-wrap gap-2"
      >
        <MButtonDropdown
          v-model="size"
          tooltip="components.dataTable.tableSizeDropdown.tooltip"
          :loading
          :items="DATA_TABLE_SIZE"
        />

        <MButtonShowHide
          v-model="activeColumns"
          :adminColumns
          :adminColumnsVisible
          :loading
          :options="initialColumns"
        />
      </div>

      <slot name="slotRight" />
    </div>
  </div>
</template>
